<script lang="ts">
	//Formatters/i18n
	import { format } from 'svelte-i18n';
	//Svelte
	import { beforeUpdate } from 'svelte';
	//Router
	import { push } from 'svelte-spa-router';
	//Stores
	import { profile_data, user_settings } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import {
		device_notifications,
		dismissed_notifications,
		read_notifications,
	} from 'src/stores/features/device-notifications';
	import { notification_log } from 'src/stores/notification_log';
	import { profiles } from 'src/stores/features/profiles';
	import { showGlobalLoader } from 'src/stores/ui/global-loader';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design System components
	import {
		Icon,
		Text,
		Button,
		ButtonV2,
		NotificationMenu,
		NotificationMenuItem,
		MenuItemList,
		IconButton,
		Token,
	} from '@Shelly/shelly-design-system';
	//Helpers
	import { dismissNotification } from 'src/helpers/notifications/device_notifications_helper';
	import {
		genNotificationItemAvatar,
		genNotificationItemBadge,
		generateNotificationHTMLContent,
		getActiveProfileID,
		getPromoNotifications,
		listenForActiveDiscounts,
		NOTIFICATION_FILTER,
		setNotificationHomeData,
		TIME_TO_CLEAR_NOTIFICATION,
		type DeviceNotificationsExtended,
		type NotificationFilterType,
	} from './notification-center-helpers';
	import {
		isNotificationLogItemId,
		mapNotificationLogEntries,
		parseNotificationLogRowId,
		type MappedNotificationLogItem,
	} from './notification-log-helpers';

	let showNotificationMenu = false;
	let notificationFilter: NotificationFilterType = NOTIFICATION_FILTER.all;
	let notificationsPromo = getPromoNotifications();
	let allNotifications: DeviceNotificationsExtended = [];

	const genNotificationItems = (
		notifications: DeviceNotificationsExtended,
		formatMessage: typeof $format,
	) => {
		return notifications.map((notification) => {
			const deviceID = notification.id.split('-')[0];
			const device = $devices[deviceID];
			const content = generateNotificationHTMLContent(
				notification.heading.global,
				notification.message,
			);

			const notificationBadge = { ...genNotificationItemBadge(notification) };
			notificationBadge.value = formatMessage(
				`label_${(notificationBadge.value as string).toLowerCase()}`,
				{
					default: notificationBadge.value as string,
				},
			);

			return {
				id: notification.id,
				circleAvatar: device ? genNotificationItemAvatar(device) : notification.circleAvatar,
				isUnread: readNotificationsIDs.includes(notification.id) ? false : true,
				//TODO It can be added in notification builder, but it is executed every second. We should store notification data to cloud, so the date to be equal to all devices where we open the profile
				date: notification.date || undefined,
				badge: notificationBadge,
				content: content,
				actions: !!notification.ctaGlobal.title,
				cta: notification.ctaGlobal,
				type: notification.type,
			};
		});
	};

	/** A rendered dropdown item, from either the device/promo pipeline or the backend log. */
	type MappedNotification = ReturnType<typeof genNotificationItems>[0] | MappedNotificationLogItem;

	const onOpenNotificationMenu = async () => {
		showNotificationMenu = !showNotificationMenu;
		if (showNotificationMenu) {
			clearMissingReadNotifications();
			// FE-1715 spike: pull the backend notification-log (new layout only).
			// Safe to call for any account — non-whitelisted users get an empty list.
			if ($showNewLayout) {
				notification_log.load();
			}
		}

		await setNotificationHomeData();
		notificationsPromo = getPromoNotifications();
	};

	const onCloseNotificationMenu = () => {
		showNotificationMenu = false;
		notificationFilter = 'all';
	};

	const onSeeAllOffers = () => {
		onCloseNotificationMenu();
		push($showNewLayout ? '/explore-shelly/discounts' : '/settings/discounts');
	};

	const onFilterNotifications = (filter: NotificationFilterType) => {
		notificationFilter = filter;
	};

	const onMarkNotificationAs = (notification: MappedNotification) => {
		// Backend notification-log items persist their state server-side.
		if (isNotificationLogItemId(notification.id)) {
			const rowId = parseNotificationLogRowId(notification.id);
			if (notification.isUnread) {
				notification_log.markSeen(rowId);
			} else {
				notification_log.markUnseen(rowId);
			}
			return;
		}

		const activeProfileID = getActiveProfileID();

		if (!notification.isUnread) {
			read_notifications.unread(activeProfileID, notification.id);
		} else {
			read_notifications.read(activeProfileID, notification.id);
		}
	};

	const clearMissingReadNotifications = () => {
		const activeProfileID = getActiveProfileID();
		readNotificationsIDs.forEach((notifiationID) => {
			// Remove notifications that are missing in device notification
			const notification = allNotifications.find((n) => n.id === notifiationID);

			const isNotificationPromo =
				!$device_notifications[notifiationID] &&
				notification?.type === NOTIFICATION_FILTER.promo;
			if (
				!$device_notifications[notifiationID] &&
				$read_notifications?.[activeProfileID]?.[notifiationID] &&
				!isNotificationPromo
			) {
				read_notifications.unread(activeProfileID, notifiationID);
			}
		});
	};

	const onMarkAllNotificationAs = () => {
		const shouldMarkRead = unreadCountTotal > 0;
		const activeProfileID = getActiveProfileID();
		genNotificationItems(allNotifications, $format).forEach((notification) => {
			if (shouldMarkRead) {
				read_notifications.read(activeProfileID, notification.id);
			} else {
				read_notifications.unread(activeProfileID, notification.id);
			}
		});
		const logRowIds = logNotifications.map((notification) =>
			parseNotificationLogRowId(notification.id),
		);
		if (logRowIds.length > 0) {
			if (shouldMarkRead) {
				notification_log.markSeen(logRowIds);
			} else {
				notification_log.markUnseen(logRowIds);
			}
		}
	};

	//Clear notification makes dismiss for 24 hours. This will be until we have api to save this data
	const onClearNotification = (notification: MappedNotification) => {
		// Backend notification-log items are dismissed server-side (state=3).
		if (isNotificationLogItemId(notification.id)) {
			notification_log.dismiss(parseNotificationLogRowId(notification.id));
			return;
		}

		dismissNotification(notification.id);
		const activeProfileID = getActiveProfileID();
		read_notifications.unread(activeProfileID, notification.id);
	};

	const onClearAllNotifications = () => {
		const activeProfileID = getActiveProfileID();
		allNotifications.forEach((notification) => {
			if (notification.type !== NOTIFICATION_FILTER.promo) {
				dismissNotification(notification.id);
				read_notifications.unread(activeProfileID, notification.id);
			}
		});
		// logNotifications already excludes dismissed entries; dismiss the rest.
		const logRowIds = logNotifications.map((notification) =>
			parseNotificationLogRowId(notification.id),
		);
		if (logRowIds.length > 0) {
			notification_log.dismiss(logRowIds);
		}
	};

	$: activeProfile = $profiles.find((item) => item.isSelected);

	$: dismissNotificationsIDs = Object.keys($dismissed_notifications);

	$: readNotificationsIDs = Object.keys($read_notifications[activeProfile?.id] || {});

	$: allNotifications = [
		...notificationsPromo,
		...Object.values($device_notifications).filter((item) => {
			return !dismissNotificationsIDs.includes(item.id);
		}),
	];

	$: deviceNotifications = allNotifications.filter((item) => item.type !== NOTIFICATION_FILTER.promo);

	$: promoNotifications = allNotifications.filter((item) => item.type === NOTIFICATION_FILTER.promo);

	$: filteredNotifications =
		notificationFilter === NOTIFICATION_FILTER.promo
			? promoNotifications
			: deviceNotifications.filter((item) =>
					notificationFilter === NOTIFICATION_FILTER.all
						? item.id
						: !readNotificationsIDs.includes(item.id),
				);

	// Backend notification-log items (new layout). Read/dismiss state is server-side.
	$: logNotifications = mapNotificationLogEntries($notification_log, $format, $devices);

	$: filteredLogNotifications =
		notificationFilter === NOTIFICATION_FILTER.promo
			? []
			: notificationFilter === NOTIFICATION_FILTER.unread
				? logNotifications.filter((item) => item.isUnread)
				: logNotifications;

	/**
	 * Sort key for the merged feed. Log items sort by their real timestamp;
	 * device/promo alerts have no timestamp today, so they resolve to "now" and
	 * stay pinned above the timestamped history.
	 */
	const getNotificationSortKey = (item: MappedNotification): number =>
		'sortTimestamp' in item && typeof item.sortTimestamp === 'number'
			? item.sortTimestamp
			: Number.MAX_SAFE_INTEGER;

	$: mappedNotifications = [
		...genNotificationItems(filteredNotifications, $format),
		...filteredLogNotifications,
	].sort((a, b) => getNotificationSortKey(b) - getNotificationSortKey(a));

	$: logUnreadCount = logNotifications.filter((item) => item.isUnread).length;

	$: allNotificationsCount = deviceNotifications.length + logNotifications.length;

	$: unreadNotificationsCount = deviceNotifications.filter(
		(item) => !readNotificationsIDs.includes(item.id),
	).length;

	$: unreadCountTotal = unreadNotificationsCount + logUnreadCount;

	$: promoNotificationsCount = promoNotifications.length;

	$: unreadPromoNotificationsCount = promoNotifications.filter(
		(item) => !readNotificationsIDs.includes(item.id),
	).length;

	$: discountsNotification = listenForActiveDiscounts($user_settings, $profile_data);

	beforeUpdate(() => {
		const time = +(new Date().getTime() / 1000).toFixed(0);
		Object.entries($dismissed_notifications).forEach(([id, ts]) => {
			if (time - Number(ts) > TIME_TO_CLEAR_NOTIFICATION) {
				dismissed_notifications.remove(id);
				dismissNotificationsIDs = dismissNotificationsIDs.filter((notifID) => notifID !== id);
			}
		});
	});
</script>

<NotificationMenu
	title={$format('settings_title_notifications', { default: 'Notifications' })}
	show={showNotificationMenu}
	showMoreActionsBtn={mappedNotifications.length > 0}
	on:close={onCloseNotificationMenu}
>
	<!-- Currently we signal for notification when whe have discountsNotification and unred notification -->
	<IconButton
		aria-label={$format('aria_label_notifications_center', { default: 'Open notifications' })}
		variant="secondary"
		shape="circle"
		icon="fas fa-bell"
		isActive={showNotificationMenu}
		notification={((!$showGlobalLoader && discountsNotification > 0) ||
			unreadCountTotal > 0 ||
			unreadPromoNotificationsCount > 0) && {
			type: 'danger',
		}}
		on:click={onOpenNotificationMenu}
	/>

	<svelte:fragment slot="dropdown-menu-items">
		<MenuItemList
			size="m"
			title={unreadCountTotal > 0
				? $format('button_mark_all_as_read', { default: 'Mark all as read' })
				: $format('button_mark_all_as_unread', { default: 'Mark all as unread' })}
			icon={unreadCountTotal > 0 ? 'fas fa-check-double' : 'fa-duotone fa-solid fa-envelope-dot'}
			iconStyle="--fa-primary-color: var(--color-danger); --fa-secondary-color: var(--color-white); --fa-secondary-opacity: 1;"
			on:click={() => {
				onMarkAllNotificationAs();
			}}
		/>
		{#if notificationFilter !== NOTIFICATION_FILTER.promo}
			<MenuItemList
				size="m"
				title={$format('button_clear_all_notifications', { default: 'Clear all notifications' })}
				icon="fas fa-times"
				on:click={() => {
					onClearAllNotifications();
				}}
			/>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="staticTop">
		<div class="d-flex flex-wrap gap-s mb-s-plus">
			<Token
				id="notyFilterAll"
				title={$format('notifications_filter_all', {
					default: 'All ({count})',
					values: { count: allNotificationsCount },
				})}
				selected={notificationFilter === NOTIFICATION_FILTER.all}
				size="m"
				type="normal"
				on:click={() => {
					onFilterNotifications(NOTIFICATION_FILTER.all);
				}}
			/>
			<Token
				id="notyFilterUnread"
				title={$format('notifications_filter_unread', {
					default: 'Unread ({count})',
					values: { count: unreadCountTotal > 0 ? unreadCountTotal : 0 },
				})}
				selected={notificationFilter === NOTIFICATION_FILTER.unread}
				size="m"
				type="normal"
				on:click={() => {
					onFilterNotifications(NOTIFICATION_FILTER.unread);
				}}
			/>

			{#if promoNotificationsCount > 0}
				<Token
					id="notyFilterPromo"
					title={$format('notifications_filter_promotions', {
						default: 'Promotions ({count})',
						values: { count: promoNotificationsCount },
					})}
					selected={notificationFilter === NOTIFICATION_FILTER.promo}
					size="m"
					type="normal"
					on:click={() => {
						onFilterNotifications(NOTIFICATION_FILTER.promo);
					}}
				/>
			{/if}
		</div>
	</svelte:fragment>

	<svelte:fragment slot="content">
		{#if mappedNotifications.length === 0}
			<div class="text-center">
				<Icon
					class="mb-m"
					size="l"
					icon={notificationFilter === NOTIFICATION_FILTER.all
						? 'fal fa-bells'
						: 'fal fa-thumbs-up'}
				/>
				<Text class="mb-s text-bold" styleType="body">
					{#if notificationFilter === NOTIFICATION_FILTER.all}
						{$format('heading_no_notifications_yet', { default: 'No notifications, yet' })}
					{:else}
						{$format('heading_no_unread_notifications', {
							default: 'No unread notifications',
						})}
					{/if}
				</Text>
				<Text class="text-color-muted" styleType="body-small">
					{#if notificationFilter === NOTIFICATION_FILTER.all}
						{$format('text_no_notifications_hint', {
							default: "When something new arrives, you'll see it here.",
						})}
					{:else}
						{$format('text_no_unread_notifications_hint', {
							default: 'You are all caught up.',
						})}
					{/if}
				</Text>
			</div>
		{:else}
			{#each mappedNotifications as notification (notification.id)}
				<NotificationMenuItem
					id={notification.id}
					circleAvatar={notification.circleAvatar}
					isUnread={notification.isUnread}
					date={notification.date}
					badge={notification.badge}
					hideSlotActions={!notification.actions && notification?.badge?.type !== 'primary'}
				>
					{@html notification.content}

					<svelte:fragment slot="dropdown-menu-items">
						<MenuItemList
							size="m"
							title={notification.isUnread
								? $format('button_mark_as_read', { default: 'Mark as read' })
								: $format('button_mark_as_unread', { default: 'Mark as unread' })}
							icon={notification.isUnread
								? 'fas fa-check'
								: 'fa-duotone fa-solid fa-envelope-dot'}
							iconStyle="--fa-primary-color: var(--color-danger); --fa-secondary-color: var(--color-white); --fa-secondary-opacity: 1;"
							on:click={() => {
								onMarkNotificationAs(notification);
							}}
						/>

						{#if notification.type !== NOTIFICATION_FILTER.promo}
							<MenuItemList
								size="m"
								title={$format('button_clear_notification', {
									default: 'Clear notification',
								})}
								icon="fas fa-times"
								on:click={() => {
									onClearNotification(notification);
								}}
							/>
						{/if}
					</svelte:fragment>

					<svelte:fragment slot="actions">
						<Button
							on:click={() => {
								if (
									notification.type === NOTIFICATION_FILTER.promo &&
									notification.isUnread
								) {
									read_notifications.read(getActiveProfileID(), notification.id);
								}
								notification.cta.onClick();
								onCloseNotificationMenu();
							}}
							size="s"
							variant={notification.type === NOTIFICATION_FILTER.promo
								? 'primary'
								: 'secondary'}
						>
							{notification.cta.title}
						</Button>
					</svelte:fragment>
				</NotificationMenuItem>
			{/each}
		{/if}

		{#if notificationFilter === NOTIFICATION_FILTER.promo && promoNotificationsCount > 0}
			<div class="see-all-offers-footer pt-s">
				<ButtonV2
					size="m"
					width="full"
					variant="secondary"
					icon="fas fa-percent"
					aria-label={$format('button_see_all_special_offers', {
						default: 'See all special offers',
					})}
					on:click={onSeeAllOffers}
				>
					{$format('button_see_all_special_offers', { default: 'See all special offers' })}
				</ButtonV2>
			</div>
		{/if}
	</svelte:fragment>
</NotificationMenu>

<style>
	.see-all-offers-footer {
		position: sticky;
		bottom: 0;
		background-color: var(--color-1);
	}

	@media (max-width: 639.98px) {
		.see-all-offers-footer {
			background-color: var(--color-base);
		}
	}
</style>
